import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ValueChangeEvent } from '@interfaces/form.interface';
import { InputComponent } from '../input/input.component';
import Quill from 'quill';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import { environment } from '@shared-envs';

const Link = (Quill as any).import('formats/link');

class CustomLink extends Link {
  static create(value: any) {
    let node = super.create(value);
    // Prepend https:// if the URL doesn't start with http:// or https://
    if (value && !/^https?:\/\//i.test(value)) {
      value = 'http://' + value;
    }
    node.setAttribute('href', value); // Set the link URL
    return node;
  }
}

Quill.register(CustomLink, true);
@Component({
  selector: 'ark-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
})
export class TextareaComponent
  extends InputComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() labelShow = true;
  @Input() rows = 10; // Default rows input
  @Input() columns = 30;
  @Input() useQuillEditor = false;
  @Input() override size: 'auto' | 'medium' | 'large' = 'auto';
  @Input() disableContextMenu = false;

  @ViewChild('quill') quill!: QuillEditorComponent;

  isFocused = false;
  charsTyped = 0;
  valueChangedSubscription: Subscription = new Subscription();
  quillModules!: QuillModules;
  quillEditorInstance: any;

  constructor(fb: UntypedFormBuilder, translate: TranslateService) {
    super(fb, translate);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.setQuillModules();

    this.valueChangedSubscription = this.valueChangedObservable
      .pipe(distinctUntilChanged())
      .subscribe((e: ValueChangeEvent) => {
        const checkIfValueisNull = e.value || ''; // If null or undefined, use an empty string

        this.charsTyped = checkIfValueisNull.length;

        environment.log('Content changed, new value length:', this.charsTyped);
        // environment.log(this.formStates.error)
      });
  }

  ngAfterViewInit(): void {
    if (this.quill && this.quill.editorElem) {
      this.quill.editorElem.addEventListener('contextmenu', (event) => {
        event.preventDefault();
      });
    }
    if (this.quillEditorInstance) {
      this.adjustQuillHeight();
    }
  }

  override ngOnDestroy(): void {
    this.valueChangedSubscription.unsubscribe();
  }

  setQuillModules() {
    this.quillModules = {
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
      ],
    };
  }

  adjustQuillHeight() {
    if (this.quillEditorInstance) {
      const quillContainer = this.quillEditorInstance.root;
      quillContainer.style.height = `${this.rows * 1.5}rem`; // Adjust height by multiplying rows by line height
    }
  }

  onQuillEditorReady(editorInstance: any) {
    this.quillEditorInstance = editorInstance;
    this.adjustQuillHeight();

    environment.log('Quill editor is ready:', editorInstance);
  }

  // Handle focus state changes
  handleFocus(focused: boolean): void {
    this.isFocused = focused;

    environment.log('Editor focus changed:', focused);
  }

  onQuillContentChange(event: any): void {
    let content = event.html || '';

    const plainText = content.replace(/<[^>]*>/g, '').trim(); // Remove tags and trim spaces

    environment.log('Plain text content:', plainText);

    // If plainText is empty, set it to an empty string
    if (!plainText) {
      this.form.get('formValue')?.setValue('', { emitEvent: false });
    } else {
      // Set the plain text value in your form or model
      this.form.get('formValue')?.setValue(plainText, { emitEvent: false });
    }

    this.charsTyped = plainText.length;
  }

  sanitizeInput(input: string): string {
    const div = document.createElement('div');
    div.innerHTML = input;
    return div.textContent || div.innerText || '';
  }
}
